<template>
  <div class="permission-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1>权限管理</h1>
      <div class="header-actions">
        <el-button type="primary" @click="handleCreatePermission" v-permission="'permission:edit'">
          <el-icon><Plus /></el-icon>
          新增权限
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和过滤 -->
    <el-card class="filter-card">
      <el-form :model="searchForm" inline>
        <el-form-item label="权限名称">
          <el-input
            v-model="searchForm.name"
            placeholder="请输入权限名称"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="权限编码">
          <el-input
            v-model="searchForm.code"
            placeholder="请输入权限编码"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="模块">
          <el-select
            v-model="searchForm.module"
            placeholder="请选择模块"
            clearable
            style="width: 150px"
          >
            <el-option
              v-for="module in modules"
              :key="module"
              :label="module"
              :value="module"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select
            v-model="searchForm.status"
            placeholder="请选择状态"
            clearable
            style="width: 120px"
          >
            <el-option label="启用" value="active" />
            <el-option label="禁用" value="inactive" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="handleReset">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 权限列表 -->
    <el-card class="table-card">
      <el-table
        :data="filteredPermissions"
        v-loading="loading"
        stripe
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="权限名称" width="150" />
        <el-table-column prop="code" label="权限编码" width="150" />
        <el-table-column prop="description" label="描述" min-width="200" />
        <el-table-column prop="module" label="模块" width="120">
          <template #default="{ row }">
            <el-tag size="small" :type="getModuleTagType(row.module)">
              {{ row.module }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag
              :type="row.status === 'active' ? 'success' : 'danger'"
              size="small"
            >
              {{ row.status === 'active' ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button
              type="primary"
              size="small"
              @click="handleEditPermission(row)"
              v-permission="'permission:edit'"
            >
              编辑
            </el-button>
            <el-popconfirm
              title="确定要删除这个权限吗？"
              confirm-button-text="确定"
              cancel-button-text="取消"
              @confirm="handleDeletePermission(row.id)"
              v-permission="'permission:edit'"
            >
              <template #reference>
                <el-button type="danger" size="small">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 创建/编辑权限对话框 -->
    <el-dialog
      v-model="permissionDialogVisible"
      :title="permissionDialogTitle"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="permissionFormRef"
        :model="permissionForm"
        :rules="permissionRules"
        label-width="100px"
      >
        <el-form-item label="权限名称" prop="name">
          <el-input
            v-model="permissionForm.name"
            placeholder="请输入权限名称"
            maxlength="50"
          />
        </el-form-item>
        <el-form-item label="权限编码" prop="code">
          <el-input
            v-model="permissionForm.code"
            placeholder="请输入权限编码"
            maxlength="50"
          />
          <div class="form-tip">格式：模块:操作，如 user:view</div>
        </el-form-item>
        <el-form-item label="模块" prop="module">
          <el-select
            v-model="permissionForm.module"
            placeholder="请选择模块"
            style="width: 100%"
          >
            <el-option
              v-for="module in modules"
              :key="module"
              :label="module"
              :value="module"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="permissionForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入权限描述"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="permissionForm.status">
            <el-radio label="active">启用</el-radio>
            <el-radio label="inactive">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="permissionDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmitPermission" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'

const store = useStore()

// 状态
const loading = ref(false)
const submitting = ref(false)
const permissionDialogVisible = ref(false)

// 搜索表单
const searchForm = reactive({
  name: '',
  code: '',
  module: '',
  status: ''
})

// 权限表单
const permissionFormRef = ref(null)
const permissionForm = reactive({
  id: null,
  name: '',
  code: '',
  description: '',
  module: '',
  status: 'active'
})

// 表单验证规则
const permissionRules = {
  name: [
    { required: true, message: '请输入权限名称', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在2到50个字符', trigger: 'blur' }
  ],
  code: [
    { required: true, message: '请输入权限编码', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在2到50个字符', trigger: 'blur' },
    { 
      pattern: /^[a-z]+:[a-z]+$/,
      message: '格式错误，应为模块:操作，如 user:view',
      trigger: 'blur'
    }
  ],
  description: [
    { required: true, message: '请输入权限描述', trigger: 'blur' },
    { min: 5, max: 200, message: '长度在5到200个字符', trigger: 'blur' }
  ],
  module: [
    { required: true, message: '请选择模块', trigger: 'change' }
  ],
  status: [
    { required: true, message: '请选择状态', trigger: 'change' }
  ]
}

// 计算属性
const permissions = computed(() => store.getters['permission/permissions'])

// 模块列表
const modules = computed(() => {
  const moduleSet = new Set()
  permissions.value.forEach(permission => {
    if (permission.module) {
      moduleSet.add(permission.module)
    }
  })
  return Array.from(moduleSet)
})

// 过滤后的权限列表
const filteredPermissions = computed(() => {
  let filtered = permissions.value
  
  if (searchForm.name) {
    filtered = filtered.filter(permission => 
      permission.name.toLowerCase().includes(searchForm.name.toLowerCase())
    )
  }
  
  if (searchForm.code) {
    filtered = filtered.filter(permission => 
      permission.code.toLowerCase().includes(searchForm.code.toLowerCase())
    )
  }
  
  if (searchForm.module) {
    filtered = filtered.filter(permission => permission.module === searchForm.module)
  }
  
  if (searchForm.status) {
    filtered = filtered.filter(permission => permission.status === searchForm.status)
  }
  
  return filtered
})

// 对话框标题
const permissionDialogTitle = computed(() => {
  return permissionForm.id ? '编辑权限' : '新增权限'
})

// 方法
const getModuleTagType = (module) => {
  const moduleColors = {
    '用户管理': 'primary',
    '债务管理': 'success',
    '任务管理': 'warning',
    '积分管理': 'danger',
    '商户管理': 'info',
    '系统管理': ''
  }
  return moduleColors[module] || 'info'
}

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    await store.dispatch('permission/fetchPermissions')
  } catch (error) {
    ElMessage.error('加载数据失败：' + error.message)
  } finally {
    loading.value = false
  }
}

// 刷新数据
const refreshData = () => {
  loadData()
}

// 搜索
const handleSearch = () => {
  // 搜索逻辑已经在computed中实现
}

// 重置搜索
const handleReset = () => {
  searchForm.name = ''
  searchForm.code = ''
  searchForm.module = ''
  searchForm.status = ''
}

// 创建权限
const handleCreatePermission = () => {
  Object.assign(permissionForm, {
    id: null,
    name: '',
    code: '',
    description: '',
    module: '',
    status: 'active'
  })
  permissionDialogVisible.value = true
  nextTick(() => {
    if (permissionFormRef.value) {
      permissionFormRef.value.clearValidate()
    }
  })
}

// 编辑权限
const handleEditPermission = (permission) => {
  Object.assign(permissionForm, {
    id: permission.id,
    name: permission.name,
    code: permission.code,
    description: permission.description,
    module: permission.module,
    status: permission.status
  })
  permissionDialogVisible.value = true
  nextTick(() => {
    if (permissionFormRef.value) {
      permissionFormRef.value.clearValidate()
    }
  })
}

// 删除权限
const handleDeletePermission = async (id) => {
  try {
    // 检查权限是否被角色使用
    const permission = permissions.value.find(p => p.id === id)
    const roles = store.getters['permission/roles']
    
    const usedByRoles = roles.filter(role => 
      role.permissions.includes(permission?.code)
    )
    
    if (usedByRoles.length > 0) {
      const roleNames = usedByRoles.map(role => role.name).join('、')
      ElMessage.warning(`该权限正在被以下角色使用：${roleNames}，请先解除关联`)
      return
    }
    
    await store.dispatch('permission/deletePermission', id)
    ElMessage.success('删除成功')
    
    // 记录操作日志
    await store.dispatch('operationLog/addLog', {
      adminId: store.getters['auth/adminInfo'].id,
      adminName: store.getters['auth/adminInfo'].name,
      action: '删除权限',
      module: '权限管理',
      target: `权限ID: ${id}`,
      details: `删除了权限: ${permission?.name}`,
      ip: '192.168.1.100',
      status: 'success'
    })
  } catch (error) {
    ElMessage.error('删除失败：' + error.message)
  }
}

// 提交权限表单
const handleSubmitPermission = async () => {
  if (!permissionFormRef.value) return
  
  try {
    await permissionFormRef.value.validate()
    submitting.value = true
    
    if (permissionForm.id) {
      // 更新权限
      await store.dispatch('permission/updatePermission', permissionForm)
      ElMessage.success('更新成功')
      
      // 记录操作日志
      await store.dispatch('operationLog/addLog', {
        adminId: store.getters['auth/adminInfo'].id,
        adminName: store.getters['auth/adminInfo'].name,
        action: '更新权限',
        module: '权限管理',
        target: `权限ID: ${permissionForm.id}`,
        details: `更新了权限: ${permissionForm.name}`,
        ip: '192.168.1.100',
        status: 'success'
      })
    } else {
      // 创建权限
      const newPermission = await store.dispatch('permission/createPermission', permissionForm)
      ElMessage.success('创建成功')
      
      // 记录操作日志
      await store.dispatch('operationLog/addLog', {
        adminId: store.getters['auth/adminInfo'].id,
        adminName: store.getters['auth/adminInfo'].name,
        action: '创建权限',
        module: '权限管理',
        target: `权限ID: ${newPermission.id}`,
        details: `创建了新权限: ${newPermission.name}`,
        ip: '192.168.1.100',
        status: 'success'
      })
    }
    
    permissionDialogVisible.value = false
    refreshData()
  } catch (error) {
    if (error.message !== 'validate') {
      ElMessage.error('操作失败：' + error.message)
    }
  } finally {
    submitting.value = false
  }
}

// 生命周期
onMounted(() => {
  loadData()
})
</script>

<style scoped>
.permission-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  color: #ffffff;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .filter-card .el-form-item {
    width: 100%;
    margin-bottom: 10px;
  }
  
  .filter-card .el-form-item .el-input,
  .filter-card .el-form-item .el-select {
    width: 100%;
  }
}
</style>
